import { EditorState, Modifier } from 'draft-js';
import { BLOCK_TYPE, INLINE_STYLE } from 'draftail';

export const INLINE_STYLES = [
  { type: INLINE_STYLE.BOLD },
  { type: INLINE_STYLE.ITALIC },
  { type: INLINE_STYLE.UNDERLINE },
];

export const BLOCK_CONTROL = {
  UNORDERED_LIST_ITEM: {
    type: BLOCK_TYPE.UNORDERED_LIST_ITEM,
  },
  ORDERED_LIST_ITEM: {
    type: BLOCK_TYPE.ORDERED_LIST_ITEM,
  },
};

const INPUT_BLOCK_MAP = {
  '* ': BLOCK_TYPE.UNORDERED_LIST_ITEM,
  '- ': BLOCK_TYPE.UNORDERED_LIST_ITEM,
  '1. ': BLOCK_TYPE.ORDERED_LIST_ITEM,
  '# ': BLOCK_TYPE.HEADER_ONE,
  '## ': BLOCK_TYPE.HEADER_TWO,
  '### ': BLOCK_TYPE.HEADER_THREE,
  '#### ': BLOCK_TYPE.HEADER_FOUR,
  '##### ': BLOCK_TYPE.HEADER_FIVE,
  '###### ': BLOCK_TYPE.HEADER_SIX,
  '> ': BLOCK_TYPE.BLOCKQUOTE,
  // It makes more sense not to require a space here.
  // This matches how Dropbox Paper operates.
  '```': BLOCK_TYPE.CODE,
};

const INPUT_STYLE_MAP = [
  // Order matters, as shorter patterns are contained in the longer ones.
  {
    pattern: '**',
    type: INLINE_STYLE.BOLD,
  },
  {
    pattern: '__',
    type: INLINE_STYLE.BOLD,
  },
  {
    pattern: '*',
    type: INLINE_STYLE.ITALIC,
  },
  {
    pattern: '_',
    type: INLINE_STYLE.ITALIC,
  },
  {
    pattern: '~~',
    type: INLINE_STYLE.STRIKETHROUGH,
  },
  {
    pattern: '~',
    type: INLINE_STYLE.STRIKETHROUGH,
  },
  {
    pattern: '`',
    type: INLINE_STYLE.CODE,
  },
].map(function (_ref) {
  const pattern = _ref.pattern;
  const type = _ref.type;
  const pat = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const char = pattern[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // https://regexper.com/#%28%5Cs%7C%5E%29__%28%5B%5E%5Cs_%5D%7B1%2C2%7D%7C%5B%5E%5Cs_%5D.%2B%5B%5E%5Cs_%5D%29__%24
  // This is stored as an escaped string instead of a RegExp object because they are stateful.
  // This regex encapsulates a few rules:
  // - The pattern must be preceded by whitespace, or be at the start of the input.
  // - The pattern must end the input.
  // - In-between the start and end patterns, there can't be only whitespace or characters from the pattern.
  // - There has to be at least 1 char that's not whitespace or the pattern’s char.

  const regex = '(\\s|^)'
    .concat(pat, '([^\\s')
    .concat(char, ']{1,2}|[^\\s')
    .concat(char, '].+[^\\s')
    .concat(char, '])')
    .concat(pat, '$');
  return {
    pattern: pattern,
    type: type,
    regex: regex,
  };
});

export default {
  events: {},
  getFixedEditorStateForServiceDeskView(editorState, blockTypes) {
    const selection = editorState.getSelection();

    if (selection.isCollapsed()) {
      const content = editorState.getCurrentContent();
      const block = content.getBlockMap().get(selection.getStartKey());
      const startOffset = selection.getStartOffset();
      const text = block.getText();
      const beforeInput = text.slice(0, startOffset);
      // eslint-disable-next-line no-var
      var newEditorState = editorState;

      const newBlockType = handleBeforeInputBlockType(text + ' ', blockTypes);

      if (newBlockType) {
        newEditorState = resetBlockWithType(newEditorState, newBlockType, '');
      }

      const newStyle = handleBeforeInputInlineStyle(beforeInput, INLINE_STYLES);

      if (newStyle) {
        newEditorState = applyMarkdownStyle(newEditorState, newStyle, ' ');
      } else {
        newEditorState = applyChar(newEditorState, ' ');
      }
    }

    return newEditorState;
  },
  addMissingCharactersToEditorInput(editorState, char) {
    return applyChar(editorState, char);
  },
};

function applyChar(editorState, char) {
  const selection = editorState.getSelection();
  let content = editorState.getCurrentContent();
  const offset = selection.getFocusOffset();
  const endSelection = selection.merge({
    anchorOffset: offset,
    focusOffset: offset,
  });
  content = content.merge({
    selectionAfter: endSelection,
  });
  content = Modifier.insertText(content, endSelection, char);
  return EditorState.push(editorState, content, 'change-inline-style');
}

function applyMarkdownStyle(editorState, range, char) {
  const selection = editorState.getSelection();
  let content = editorState.getCurrentContent();
  const marked = selection.merge({
    anchorOffset: range.start,
    focusOffset: range.end,
  });
  const endMarker = selection.merge({
    anchorOffset: range.end - range.pattern.length,
    focusOffset: range.end,
  });
  const startMarker = selection.merge({
    anchorOffset: range.start,
    focusOffset: range.start + range.pattern.length,
  }); // Remove the markers separately to preserve existing styles and entities on the marked text.

  content = Modifier.applyInlineStyle(content, marked, range.type);
  content = Modifier.removeRange(content, endMarker, 'forward');
  content = Modifier.removeRange(content, startMarker, 'forward');
  const offset = selection.getFocusOffset() - range.pattern.length * 2;
  const endSelection = selection.merge({
    anchorOffset: offset,
    focusOffset: offset,
  });
  content = content.merge({
    selectionAfter: endSelection,
  });
  content = Modifier.insertText(content, endSelection, char);
  return EditorState.push(editorState, content, 'change-inline-style');
}

function handleBeforeInputInlineStyle(input, inlineStyles) {
  const activeShortcuts = INPUT_STYLE_MAP.filter(function (_ref) {
    const type = _ref.type;
    return inlineStyles.some(function (s) {
      return s.type === type;
    });
  });
  let range;
  const match = activeShortcuts.find(function (_ref2) {
    const regex = _ref2.regex;
    // Re-create a RegExp object every time because RegExp is stateful.
    range = new RegExp(regex, 'g').exec(input);
    return range;
  });
  return range && match
    ? {
        pattern: match.pattern,
        start: range.index === 0 ? 0 : range.index + 1,
        end: range.index + range[0].length,
        type: match.type,
      }
    : false;
}

function handleBeforeInputBlockType(mark, blockTypes) {
  return blockTypes.find(function (b) {
    return b.type === INPUT_BLOCK_MAP[mark];
  })
    ? INPUT_BLOCK_MAP[mark]
    : false;
}

function resetBlockWithType(editorState, newType, newText) {
  const newData =
    // eslint-disable-next-line prefer-rest-params
    arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const key = selectionState.getStartKey();
  const blockMap = contentState.getBlockMap();
  const block = blockMap.get(key); // Maintain persistence in the list while removing chars from the start.
  // https://github.com/facebook/draft-js/blob/788595984da7c1e00d1071ea82b063ff87140be4/src/model/transaction/removeRangeFromContentState.js#L333

  let chars = block.getCharacterList();
  let startOffset = 0;
  const sliceOffset = block.getText().length - newText.length;

  while (startOffset < sliceOffset) {
    chars = chars.shift();
    startOffset += 1;
  }

  const newBlock = block.merge({
    type: newType,
    text: newText,
    characterList: chars,
    data: newData,
  });
  const newContentState = contentState.merge({
    blockMap: blockMap.set(key, newBlock),
  });
  const newSelectionState = selectionState.merge({
    anchorOffset: 0,
    focusOffset: 0,
  });
  return EditorState.acceptSelection(
    EditorState.set(editorState, {
      currentContent: newContentState,
    }),
    newSelectionState
  );
}
